import React, { useState, useContext, useRef, useEffect } from 'react';
// eslint-disable-next-line
import { useDrop } from 'react-dnd';
import {
  axios,
  dataFetcher,
  endpoints,
  firebaseCredentials,
  baseUrl,
} from 'Api';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  VideoLibrary as AllVideosIcon,
  Add as AddIcon,
} from '@material-ui/icons';
import { AppContext, AuthContext } from 'Context';
import CreateCategoryPage from '../../content-layout/CreateCategory';
import styles from './CategoriesSidebar.module.css';

const CategoriesSidebar = ({
  categories = [],
  selectedCategory,
  onCategorySelect,
  loading = false,
  content,
  setIsDummyLoader,
  isDummyLoader,
  paginatedData,
  fetchContent,
  setSelectedItems
}) => {
  const { defaultCategoryName } = useContext(AppContext);
  const { getToken } = useContext(AuthContext);
  const countWithContentId = content.filter((item) => item.content_id).length;
  const countWithoutContentId = content.filter((item) => !item.content_id).length;
  const sidebarRef = useRef(null);
  const menuListRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  const renderVideoCounts = () => {
    const uploadedCount = countWithContentId;
    const uploadingCount = countWithoutContentId;

    if (uploadedCount === 0 && uploadingCount === 0) return null;

    return (
      <div className={styles.countContainer}>
        {uploadedCount > 0 && (
          <span className={styles.menuCount}>{uploadedCount}</span>
        )}
        {uploadingCount > 0 && (
          <span className={`${styles.menuCount} ${styles.uploadingCount}`}>
            {uploadingCount}
            {' '}
            uploading
          </span>
        )}
      </div>
    );
  };
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [draggedOverCategory, setDraggedOverCategory] = useState(null);

  const handleCategoryClick = (category) => {
    onCategorySelect(category);
    setIsDummyLoader(true);
  };

  const handleAllCategoriesClick = () => {
    onCategorySelect(null);
    setIsDummyLoader(true);
  };
  const changeCategory = async (selectedItems, draggedOverCategory) => {
    setIsDummyLoader(true);
    try {
      // Prepare draft_ids array
      const draftIds = selectedItems?.map((item) => item?.draft_id);

      // Prepare payload
      const payload = {
        token: getToken(), // your token function
        category_id: draggedOverCategory?.value,
        draft_ids: draftIds
      };

      // Make POST request
      const response = await axios.post(`${baseUrl}/changeCategory`, payload);
      setSelectedItems([]);
      fetchContent();
      return response.data;
    } catch (error) {
      // Handle error
      console.error('Error changing category:', error.response?.data || error.message);
      setSelectedItems([]);
      fetchContent();
      throw error;
    }
  };
  const [{ isOver }, drop] = useDrop({
    accept: 'VIDEO_CARD',
    drop: (item, monitor) => {
      console.log('Drop triggered with item:', item);

      // Clear scroll interval on drop
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }

      // 🚫 Prevent dropping into default category
      if (!draggedOverCategory || draggedOverCategory?.name === defaultCategoryName) {
        console.warn('Drop ignored: Cannot move to default category');
        setDraggedOverCategory(null);
        return;
      }

      // 🚫 Prevent dropping to same category
      if (item.selectedItems && item.selectedItems.length > 0) {
        // Check if all selected items are already in the target category
        const allItemsInSameCategory = item.selectedItems.every(
          (video) => video.category === draggedOverCategory?.name
            || video.category_name === draggedOverCategory?.name
            || video.category_id === draggedOverCategory?.value
        );

        if (allItemsInSameCategory) {
          console.warn('Drop ignored: Items are already in this category');
          setDraggedOverCategory(null);
          return;
        }

        console.log('All selected items:', item.selectedItems);
        console.log('Total items being moved:', item.count);

        const itemTitles = item.selectedItems.map(
          (video) => video.title || video.video_title || 'Untitled'
        );
        console.log('Item titles:', itemTitles);
      }

      // ✅ Only valid drops reach here
      changeCategory(item?.selectedItems, draggedOverCategory);
      console.log('draggedOverCategory:', draggedOverCategory);
      setDraggedOverCategory(null);
      return { categoryId: draggedOverCategory };
    },

    hover: (item, monitor) => {
      // Get the hover target element
      const hoverBoundingRect = monitor.getClientOffset();
      if (hoverBoundingRect && menuListRef.current) {
        // Auto-scroll logic
        const menuListElement = menuListRef.current;
        const menuListRect = menuListElement.getBoundingClientRect();
        const scrollThreshold = 100; // pixels from top/bottom to trigger scroll
        const scrollSpeed = 30; // pixels to scroll per interval

        const mouseY = hoverBoundingRect.y;
        const distanceFromTop = mouseY - menuListRect.top;
        const distanceFromBottom = menuListRect.bottom - mouseY;

        // Clear any existing scroll interval
        if (scrollIntervalRef.current) {
          clearInterval(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }

        // Scroll up if near top
        if (distanceFromTop < scrollThreshold && distanceFromTop >= 0 && menuListElement.scrollTop > 0) {
          scrollIntervalRef.current = setInterval(() => {
            if (menuListElement.scrollTop > 0) {
              menuListElement.scrollTop -= scrollSpeed;
            } else {
              clearInterval(scrollIntervalRef.current);
              scrollIntervalRef.current = null;
            }
          }, 50);
        } else if (distanceFromBottom < scrollThreshold && distanceFromBottom >= 0) {
          // Scroll down if near bottom
          const maxScroll = menuListElement.scrollHeight - menuListElement.clientHeight;
          if (menuListElement.scrollTop < maxScroll) {
            scrollIntervalRef.current = setInterval(() => {
              if (menuListElement.scrollTop < maxScroll) {
                menuListElement.scrollTop += scrollSpeed;
              } else {
                clearInterval(scrollIntervalRef.current);
                scrollIntervalRef.current = null;
              }
            }, 50);
          }
        }

        // Find which category element is being hovered over
        const element = document.elementFromPoint(hoverBoundingRect.x, hoverBoundingRect.y);
        const categoryElement = element?.closest('[data-category-id]');

        if (categoryElement) {
          const categoryId = categoryElement.getAttribute('data-category-id');
          const matchedCategory = categoryId === 'all' ? null : categories.find((cat) => cat.value === categoryId);
          setDraggedOverCategory(matchedCategory || null);
        }
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  // Cleanup scroll interval when component unmounts or drag ends
  useEffect(() => () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  // Clear scroll interval when not hovering
  useEffect(() => {
    if (!isOver && scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, [isOver]);

  return (
    <div
      ref={(node) => {
        drop(node);
        sidebarRef.current = node;
      }}
      className={styles.sidebar}
      style={{
        backgroundColor: isOver ? 'rgba(33, 150, 243, 0.05)' : 'transparent',
        border: isOver ? '2px solid rgba(33, 150, 243, 0.3)' : '2px solid transparent',
        borderRadius: '12px',
        transition: 'all 0.3s ease-in-out',
        transform: isOver ? 'scale(1.01)' : 'scale(1)',
        boxShadow: isOver ? '0 8px 25px rgba(33, 150, 243, 0.15)' : 'none'
      }}
    >
      <div ref={menuListRef} className={styles.menuList}>
        {/* All Categories Item */}
        <div
          className={`${styles.menuItem} ${!selectedCategory ? styles.active : ''} ${draggedOverCategory === null && isOver ? styles.categoryDragOver : ''
            }`}
          data-category-id="all"
          onClick={handleAllCategoriesClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleAllCategoriesClick();
            }
          }}
          style={{
            // 🚫 Disable drop visual for default category
            border:
              draggedOverCategory === null && isOver
                ? '2px dotted transparent'
                : '2px dotted transparent',
            borderRadius: '8px',
            transform: 'scale(1)',
            cursor: 'pointer',
            opacity: 1,
            transition: 'border 0.2s ease-in-out, transform 0.2s ease-in-out'
          }}
        >
          <AllVideosIcon className={styles.menuIcon} />
          <div className={styles.menuContent}>
            <span className={styles.menuLabel}>{defaultCategoryName}</span>
            {!selectedCategory && (
              <span className={styles.menuCount}>

                {paginatedData?.video?.total_count}
              </span>
            )}

          </div>
        </div>

        {/* Category Items */}
        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.skeletonItem}>
              <div className={styles.skeletonIcon} />
              <div className={styles.skeletonText} />
            </div>
            <div className={styles.skeletonItem}>
              <div className={styles.skeletonIcon} />
              <div className={styles.skeletonText} />
            </div>
            <div className={styles.skeletonItem}>
              <div className={styles.skeletonIcon} />
              <div className={styles.skeletonText} />
            </div>
          </div>
        ) : categories.length === 0 ? (
          <div className={styles.emptyState}>
            <FolderIcon className={styles.emptyIcon} />
            <p>No categories found</p>
          </div>
        ) : (
          categories
            .filter((category) => category?.name !== defaultCategoryName)
            .map((category) => {
              const isSelected = selectedCategory?.value === category.value;
              return (
                <div
                  key={category.value}
                  className={`${styles.menuItem} ${isSelected ? styles.active : ''} ${(draggedOverCategory === category || draggedOverCategory === category?.value) && isOver ? styles.categoryDragOver : ''}`}
                  data-category-id={category.value}
                  onClick={() => handleCategoryClick(category)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleCategoryClick(category);
                    }
                  }}
                  style={{
                    border: draggedOverCategory === category && isOver ? '2px dotted #2196f3' : '2px dotted transparent',
                    borderRadius: '8px',
                    transform: draggedOverCategory === category && isOver ? 'scale(1.1)' : 'scale(1)',
                    transition: 'border 0.2s ease-in-out, transform 0.2s ease-in-out'
                  }}
                >
                  {isSelected ? (
                    <FolderOpenIcon className={styles.menuIcon} />
                  ) : (
                    <FolderIcon className={styles.menuIcon} />
                  )}
                  <div className={styles.menuContent}>
                    <span className={styles.menuLabel}>
                      {category?.name}
                    </span>
                    {isSelected && !isDummyLoader && (
                      <span className={styles.menuCount}>

                        {paginatedData?.video?.total_count}
                      </span>
                    )}
                    {/* <span className={styles.menuCount}>{category.count || 0}</span> */}
                  </div>
                </div>
              );
            })
        )}

        {/* Add Category Button */}
        <div
          className={`${styles.menuItem} ${styles.addCategoryButton}`}
          onClick={() => setShowCreateCategory(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setShowCreateCategory(true);
            }
          }}
        >
          <AddIcon className={styles.menuIcon} />
          <div className={styles.menuContent}>
            <span className={styles.menuLabel}>Add Category</span>
          </div>
        </div>
      </div>

      {/* Create Category Modal */}
      {showCreateCategory && (
        <CreateCategoryPage
          initialOpen
          onModalClose={() => setShowCreateCategory(false)}
        />
      )}
    </div>
  );
};

export default CategoriesSidebar;
